'use client'

import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { LuChevronRight, LuPlus, LuUserRound } from 'react-icons/lu'

import { Button } from '@/core/components/form/button/button'
import { cn } from '@/core/utils/common'

import { SheetHead } from '@/features/veluvit/components/overlay/layer-parts'
import Logo from '@/features/veluvit/components/shell/logo'
import { NAV } from '@/features/veluvit/components/shell/nav-items'
import TabIcon, {
  type TabKey,
} from '@/features/veluvit/components/shell/tab-icon'
import { GemIcon } from '@/features/veluvit/components/ui/currency-icon'
import GemCount from '@/features/veluvit/components/ui/gem-count'
import { openCharge } from '@/features/veluvit/services/wallet.actions'
import { openSheet } from '@/features/veluvit/stores/overlay.store'
import { useSessionStore } from '@/features/veluvit/stores/session.store'

import type { Route } from 'next'
import type { ReactNode } from 'react'

/* 모바일 메뉴 서랍 — 하단 탭 맨 왼쪽 ☰ 이 연다. 목록은 nav-items 의 NAV 에서 가져온다(하단 탭에 없는 미션 · 작품 만들기도 여기서).
   메뉴를 누르면 경로가 바뀌고, 앱 셸이 경로가 바뀔 때 레이어를 모두 닫는다(따로 닫지 않는다). */

type DrawerItem = {
  key: string
  href: Route
  label: string
  icon: ReactNode
  on: (path: string) => boolean
}

function items(): DrawerItem[] {
  const icon = (k: TabKey) => <TabIcon k={k} />
  return [
    {
      key: 'char',
      href: NAV.char.href,
      label: NAV.char.label,
      icon: icon('char'),
      on: (p) => p === '/',
    },
    // { key: 'ranking', href: NAV.ranking.href, label: NAV.ranking.label, icon: icon('ranking'), on: (p) => p.startsWith('/ranking') },
    {
      key: 'explore',
      href: NAV.explore.href,
      label: NAV.explore.label,
      icon: icon('explore'),
      on: (p) => p.startsWith('/explore'),
    },
    {
      key: 'chats',
      href: NAV.chats.href,
      label: NAV.chats.label,
      icon: icon('chats'),
      on: (p) => p === '/chats',
    },
    // [2차 개발] 미션 — 2026-10-08 2차 범위로 넘김
    // { key: 'mission', href: NAV.mission.href, label: NAV.mission.label, icon: icon('mission'), on: (p) => p.startsWith('/mission') },
    {
      key: 'studio',
      href: NAV.studio.href,
      label: '작품 만들기',
      icon: icon('studio'),
      on: (p) => p.startsWith('/studio-renew'),
    },
  ]
}

function NavDrawer() {
  const pathname = usePathname()
  const data = useSessionStore((state) => state.data)
  const user = data?.user && !data.user.guest ? data.user : null

  return (
    <>
      <SheetHead title={<Logo className="text-c20" />} />
      {/* 계정 줄 — 로그인 여부와 상관없이 마이로 간다(로그인은 마이 화면에서) */}
      <Link
        href="/my"
        aria-current={pathname === '/my' ? 'page' : undefined}
        className="mb-3 flex items-center gap-3 rounded-panel bg-foreground/[0.06] p-3 no-underline transition-colors hover:bg-foreground/[0.1]"
      >
        <span className="grid size-10 flex-none place-items-center rounded-full bg-foreground/10 text-t1">
          <LuUserRound aria-hidden="true" className="size-5" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-c15 font-bold text-t1">
            {user ? user.name : '로그인하기'}
          </span>
          <span className="block truncate text-c13 text-t3">
            {user ? '마이 · 내 재화 · 설정' : '대화 기록을 계정에 남겨요'}
          </span>
        </span>
        <LuChevronRight
          aria-hidden="true"
          className="size-4.5 flex-none text-t3"
        />
      </Link>
      <Button
        variant="ghost"
        shape="field"
        className="mb-3 w-full"
        onClick={openCharge}
      >
        <span className="flex w-full items-center justify-between gap-3">
          <span className="inline-flex min-w-0 items-center gap-2">
            <GemIcon className="size-6 flex-none" />
            <span className="sr-only">보유 젬 </span>
            <GemCount className="font-bold tabular-nums text-gem" />
            <span className="text-t3">젬</span>
          </span>
          <span className="inline-flex flex-none items-center gap-1 text-t2">
            충전
            <LuPlus aria-hidden="true" className="size-4" />
          </span>
        </span>
      </Button>
      <nav aria-label="전체 메뉴">
        <ul className="grid gap-0.5">
          {items().map((item) => {
            const on = item.on(pathname)
            return (
              <li key={item.key}>
                <Link
                  href={item.href}
                  aria-current={on ? 'page' : undefined}
                  className={cn(
                    'group flex h-12 items-center gap-3.5 rounded-field px-3 text-c16 font-semibold no-underline transition-colors duration-150',
                    on
                      ? 'bg-foreground/10 text-t1'
                      : 'text-t2 hover:bg-foreground/[0.06] hover:text-t1'
                  )}
                >
                  <span
                    aria-hidden="true"
                    className="grid size-6 flex-none place-items-center"
                  >
                    {item.icon}
                  </span>
                  {item.label}
                </Link>
              </li>
            )
          })}
        </ul>
      </nav>
    </>
  )
}

/** 햄버거 버튼이 연다 */
export const openNavDrawer = () =>
  openSheet(<NavDrawer />, {
    side: 'left',
    label: '전체 메뉴',
    key: 'nav-drawer',
  })
